import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to sync Active Directory with Monosign',
    metaDescription:
      'Connect AD as a Monosign source: create the source, map attributes, run sync, enable delta. Four-step walkthrough with deep-link to docs.',
    heroEyebrow: 'How-to · Active Directory sync',
    heroTitle: ['AD into Monosign,', 'in four moves.'],
    heroBody:
      'Treat Active Directory as the canonical source while Monosign sits on top and feeds every downstream app. Delta sync keeps profiles fresh without nightly batches.',
    badges: ['Delta sync', 'Per-OU base DN', '~4 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the full docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'Source. Mapping. Sync. Delta.',
    stepsBody: 'The lifecycle of an AD source: from create to steady-state delta sync.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Create the AD source in Monosign',
        body: 'In the admin console, add a new directory source of type Active Directory. Point it at your DC, supply the service account credentials, and specify the base DN you want to sync.',
        hint: 'Use a dedicated read-only service account for sync; never reuse a domain admin.',
      },
      {
        title: 'Map attributes',
        body: 'Map AD attributes (sAMAccountName, mail, displayName, department, groups) to the Monosign identity model. The defaults handle the most common cases, but every org has a few custom fields.',
      },
      {
        title: 'Run the initial sync',
        body: 'Trigger the full sync. Monosign pulls the user objects, normalizes them, and applies your attribute mapping. The progress is visible in the dashboard and replayable in the audit log.',
        hint: 'Start with a small OU to validate the mapping, then expand the base DN.',
      },
      {
        title: 'Enable delta sync',
        body: 'Switch from full to delta sync once the initial run completes. AD changes flow into Monosign on a few-minute cadence; webhook-triggered changes can fire in near-real-time.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Directory & sources', description: 'The full sources story across AD, LDAP, OUD, WebAPI.', href: '/sign/directory' },
      { label: 'Monosync Connectors', description: 'Push identity changes back out to apps.', href: '/sync/connectors' },
      { label: 'For IT Admins', description: 'Lifecycle, JML, dashboards, audits.', href: '/for/it-admins' },
    ],
  },
  tr: {
    metaTitle: 'Active Directory Monosign ile nasıl senkronize edilir',
    metaDescription:
      "AD'yi Monosign kaynağı olarak bağlayın: kaynağı oluşturun, öznitelikleri eşleyin, senkronizasyonu çalıştırın, delta'yı etkinleştirin. Dört adımlık rehber ve doküman bağlantısı.",
    heroEyebrow: 'Rehber · Active Directory senkronizasyonu',
    heroTitle: ['Dört hamlede', "AD'den Monosign'a."],
    heroBody:
      "Active Directory'yi kanonik kaynak olarak tutun; Monosign üstte oturur ve tüm aşağı akış uygulamalarını besler. Delta senkronizasyonu, gece toplu işleri olmadan profilleri güncel tutar.",
    badges: ['Delta senkronizasyon', 'OU bazında base DN', '~4 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'Kaynak. Eşleme. Senkronizasyon. Delta.',
    stepsBody: 'Bir AD kaynağının yaşam döngüsü: oluşturmadan kararlı delta senkronizasyonuna.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "Monosign'da AD kaynağını oluşturun",
        body: "Yönetim konsolunda Active Directory türünde yeni bir dizin kaynağı ekleyin. DC'nize yönlendirin, servis hesabı kimlik bilgilerini girin ve senkronize etmek istediğiniz base DN'i belirtin.",
        hint: 'Senkronizasyon için salt okunur, özel bir servis hesabı kullanın; asla bir domain admin hesabını yeniden kullanmayın.',
      },
      {
        title: 'Öznitelikleri eşleyin',
        body: 'AD özniteliklerini (sAMAccountName, mail, displayName, department, gruplar) Monosign kimlik modeline eşleyin. Varsayılanlar en yaygın durumları karşılar, ancak her kurumun birkaç özel alanı vardır.',
      },
      {
        title: 'İlk senkronizasyonu çalıştırın',
        body: "Tam senkronizasyonu tetikleyin. Monosign kullanıcı nesnelerini çeker, normalize eder ve öznitelik eşlemenizi uygular. İlerleme dashboard'da görünür ve denetim logundan yeniden izlenebilir.",
        hint: "Eşlemeyi doğrulamak için küçük bir OU ile başlayın, sonra base DN'i genişletin.",
      },
      {
        title: 'Delta senkronizasyonu etkinleştirin',
        body: "İlk çalıştırma tamamlanınca tam senkronizasyondan delta senkronizasyona geçin. AD değişiklikleri birkaç dakikalık aralıklarla Monosign'a akar; webhook tetikli değişiklikler neredeyse gerçek zamanlı işlenebilir.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Dizin ve kaynaklar', description: 'AD, LDAP, OUD, WebAPI genelinde kaynakların tamamı.', href: '/sign/directory' },
      { label: 'Monosync bağlayıcıları', description: 'Kimlik değişikliklerini uygulamalara geri iletin.', href: '/sync/connectors' },
      { label: 'BT yöneticileri için', description: "Yaşam döngüsü, JML, dashboard'lar, denetimler.", href: '/for/it-admins' },
    ],
  },
}

type Props = { locale: string }

export default function ActiveDirectorySyncPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/active-directory-sync"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/configuration-directory-sources-ad' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/configuration-directory-sources-ad"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
